import { ApolloProvider } from '@apollo/client'
import * as Sentry from '@sentry/react'
import { ConfirmProvider } from 'material-ui-confirm'
import { Suspense, useEffect, useState } from 'react'
import { IntercomProvider } from 'react-use-intercom'
import AuthProvider from './api/AuthProvider'
import getClient from './api/client'
import AppErrorFallback from './components/AppErrorFallback'
import NotistackProvider from './components/NotistackProvider'
import { ProgressBarStyle } from './components/ProgressBar'
import MotionLazyContainer from './components/animate/MotionLazyContainer'
import { CONFIRM_DEFAULTS } from './confirmDefaults'
import Loadable from './routes/Loadable'
import { lazyScreen as lazy } from './routes/lazyScreen'
import ThemeProvider from './theme'

// The first chunk of all, with nothing on screen yet, so this one boots behind
// the full boot screen. Every screen below it loads behind `ContentLoading`.
const Router = Loadable(
  lazy(() => import('./routes')),
  { fallback: 'app' },
)

// Lazy for one reason: it is the only static path from the entry to
// `utils/posthog`, and posthog-js is 87 kB gzip — a fifth of a render-blocking
// entry chunk that draws an empty page until it has run. Mounted below only
// once posthog has started, because a capture that arrives before `init` is
// dropped on the floor, and the first page view of a session is the one that
// says how the operator got here.
const ScrollToTop = lazy(() => import('./components/ScrollToTop'))

// What went wrong before Sentry existed. `Sentry.init` used to run here, at
// module scope, and installed its own handlers for exactly these two events;
// it now runs after the first paint so its 44 kB leave the entry chunk. These
// two listeners hold the gap open, from the same point in the boot, and the
// contents are handed to the client the moment it is up. Without them the
// deferral would quietly cost every boot-time error, which is the class of
// error that matters most.
const bootErrors: unknown[] = []

const recordBootError = (event: ErrorEvent | PromiseRejectionEvent) => {
  bootErrors.push('reason' in event ? event.reason : event.error)
}

window.addEventListener('error', recordBootError as EventListener)
window.addEventListener('unhandledrejection', recordBootError as EventListener)

const stopRecordingBootErrors = () => {
  window.removeEventListener('error', recordBootError as EventListener)
  window.removeEventListener(
    'unhandledrejection',
    recordBootError as EventListener,
  )
}

const client = getClient()
const INTERCOM_APP_ID = 's1r8dsyy'

const App = () => {
  // False until posthog-js has loaded and `init` has run. `ScrollToTop` reports
  // the page view and is gated on it, so the ordering that module-scope
  // initialisation used to give for free survives the deferral.
  const [analyticsStarted, setAnalyticsStarted] = useState(false)

  useEffect(() => {
    void import('./sentryBoot').then(({ startSentry }) => {
      // Stop first, then start: the two must never both be listening, or a
      // boot error arriving in between is reported twice.
      stopRecordingBootErrors()
      startSentry(bootErrors.splice(0))
    })
  }, [])

  useEffect(() => {
    let cancelled = false

    void import('./utils/posthog').then(({ initializePostHog }) => {
      initializePostHog()
      if (!cancelled) {
        setAnalyticsStarted(true)
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <ThemeProvider>
      <IntercomProvider
        appId={INTERCOM_APP_ID}
        autoBoot
        autoBootProps={{
          hideDefaultLauncher: true,
        }}
      >
        <NotistackProvider>
          <MotionLazyContainer>
            <ProgressBarStyle />
            <Suspense fallback={null}>
              {analyticsStarted && <ScrollToTop />}
            </Suspense>
            {/*
              With the defaults stated. Without them every `useConfirm` in the
              console drew its way out and its consequence as the same text
              button, and four screens had each worked that out separately.
              See `confirmDefaults`.
            */}
            <ConfirmProvider defaultOptions={CONFIRM_DEFAULTS}>
              <ApolloProvider client={client}>
                <AuthProvider>
                  {/*
                    The backstop. Every lazily loaded screen already carries its
                    own boundary, in `routes/Loadable`, so a screen that throws
                    is replaced on its own and the sidebar stays usable. This
                    one catches what happens outside all of them.
                  */}
                  <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
                    <Router />
                  </Sentry.ErrorBoundary>
                </AuthProvider>
              </ApolloProvider>
            </ConfirmProvider>
          </MotionLazyContainer>
        </NotistackProvider>
      </IntercomProvider>
    </ThemeProvider>
  )
}

export default App
